import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatBRL, fileUrl, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Trash, Plus, PencilSimple, Pause, Play, EyeSlash, Eye, CheckSquare, Square, Stack, X, PuzzlePiece, Copy, DotsThreeVertical } from "@phosphor-icons/react";
import useFocusRefresh from "../lib/useFocusRefresh";
import usePullToRefresh from "../lib/usePullToRefresh";
import PullIndicator from "../components/PullIndicator";
import PauseListingModal from "../components/PauseListingModal";
import BulkRulesModal from "../components/BulkRulesModal";
import ConfirmDialog, { BulkProgressOverlay } from "../components/ConfirmDialog";
import { unitLabelForItem } from "../lib/pricing";

export default function MyListings() {
  // V18.1 — hidrata do cache pra evitar "sem anúncios" ao voltar
  const MY_CACHE_KEY = "aj_my_listings_v1";
  const [items, setItems] = useState(() => {
    try {
      const raw = sessionStorage.getItem(MY_CACHE_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      return Array.isArray(parsed?.data) ? parsed.data : [];
    } catch { return []; }
  });
  const [showInactive, setShowInactive] = useState(true);
  const [pauseTarget, setPauseTarget] = useState(null);
  const nav = useNavigate();
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState([]);
  const [showBulk, setShowBulk] = useState(false);
  // V17.7: Confirmação e progresso para ações individuais e em lote
  const [confirm, setConfirm] = useState(null); // { type: 'delete'|'duplicate'|'bulk-delete'|'bulk-duplicate', ids: [] }
  const [bulkProgress, setBulkProgress] = useState(null); // { title, total, current, ok, fail }
  // V17.10 — Ações expandidas por card (removido long-press: Editar/Excluir agora ficam visíveis
  // no próprio card via botão de expansão).
  const [expandedId, setExpandedId] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/my/listings");
      setItems(r.data || []);
      try { sessionStorage.setItem(MY_CACHE_KEY, JSON.stringify({ data: r.data || [], ts: Date.now() })); } catch { /* ignore */ }
    } catch (e) {
      // V18.1 — em erro NÃO zera a lista (preserva o que já estava em tela)
      console.error("[MyListings] load failed", e?.message);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useFocusRefresh(load, []);
  const ptr = usePullToRefresh(load);

  const del = async (id) => {
    try {
      await api.delete(`/listings/${id}`);
      toast.success("Excluído");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  // Reativar é direto; pausar abre o modal (indefinida ou agendada)
  const reactivate = async (it) => {
    try {
      await api.post(`/listings/${it.id}/pause`, { mode: "reactivate" });
      toast.success("Anúncio reativado");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  // V17.5+: duplicar anúncio inteiro (novo rascunho INATIVO — dono reconfirma endereço/foto/preço).
  const duplicate = async (listing_id) => {
    try {
      const r = await api.post(`/listings/${listing_id}/duplicate`);
      toast.success(`Cópia criada como rascunho pausado. Ajuste e reative.`);
      // Redireciona pro form de edição da cópia
      nav(`/editar-anuncio/${r.data.id}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Falha ao duplicar");
    }
  };

  // V17.7: executa uma ação em lote com barra de progresso visível
  const runBulk = async ({ ids, title, fn, successMsg }) => {
    setBulkProgress({ title, total: ids.length, current: 0, ok: 0, fail: 0 });
    let ok = 0, fail = 0;
    for (let i = 0; i < ids.length; i++) {
      try { await fn(ids[i]); ok++; } catch { fail++; }
      setBulkProgress((p) => (p ? { ...p, current: i + 1, ok, fail } : p));
    }
    // pequena pausa pra o usuário ver o 100%
    await new Promise((r) => setTimeout(r, 400));
    setBulkProgress(null);
    if (ok) toast.success(successMsg(ok));
    if (fail) toast.error(`${fail} falha(s) na operação`);
    exitSelectMode();
    load();
  };

  // V17.6+: seleção em massa — duplicar todos os selecionados (com progresso)
  const bulkDuplicate = () => runBulk({
    ids: selected,
    title: "Duplicando anúncios",
    fn: (id) => api.post(`/listings/${id}/duplicate`),
    successMsg: (n) => `${n} cópia(s) criada(s) em Meus Anúncios (pausadas).`,
  });

  // V17.6+: seleção em massa — excluir todos os selecionados (com progresso)
  const bulkDelete = () => runBulk({
    ids: selected,
    title: "Excluindo anúncios",
    fn: (id) => api.delete(`/listings/${id}`),
    successMsg: (n) => `${n} anúncio(s) excluído(s).`,
  });

  const toggleSelect = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const exitSelectMode = () => { setSelectMode(false); setSelected([]); };

  // V7.1: anúncios banidos NÃO aparecem para o usuário (e sem botão de reativar)
  const filteredItems = items.filter((i) => i.removal_reason !== "admin_banned" && i.removal_reason !== "banned");
  const visibleItems = showInactive ? filteredItems : filteredItems.filter((i) => i.is_active);

  return (
    <div className="max-w-3xl mx-auto px-4 py-5 pb-28" data-testid="my-listings-page">
      <PullIndicator pulling={ptr.isPulling} progress={ptr.progress} refreshing={ptr.isRefreshing} />
      <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
        <h1 className="font-heading font-black text-2xl">Meus Anúncios</h1>
        <div className="flex items-center gap-2">
          {selectMode ? (
            <button onClick={exitSelectMode} className="text-xs px-3 py-1.5 rounded-full border-2 border-slate-200 text-slate-600 inline-flex items-center gap-1" data-testid="exit-select-mode">
              <X size={12} weight="bold" /> Cancelar
            </button>
          ) : (
            <>
              <Link
                to="/meus-adicionais"
                className="text-xs px-3 py-1.5 rounded-full border-2 border-[#004E98]/30 text-[#004E98] inline-flex items-center gap-1 bg-[#004E98]/5"
                data-testid="link-my-complements">
                <PuzzlePiece size={12} weight="bold" /> Meus adicionais
              </Link>
              <button
                onClick={() => setSelectMode(true)}
                className="text-xs px-3 py-1.5 rounded-full border-2 border-slate-200 text-slate-600 inline-flex items-center gap-1"
                data-testid="select-multiple-btn">
                <Stack size={12} weight="bold" /> Selecionar vários
              </button>
              <button
                onClick={() => setShowInactive((s) => !s)}
                className="text-xs px-3 py-1.5 rounded-full border-2 border-slate-200 text-slate-600 inline-flex items-center gap-1"
                data-testid="toggle-show-inactive">
                {showInactive ? <EyeSlash size={12} weight="bold" /> : <Eye size={12} weight="bold" />}
                {showInactive ? "Ocultar pausados" : "Mostrar pausados"}
              </button>
              <Link to="/novo-anuncio" className="btn-primary text-sm py-2 px-4" data-testid="new-listing-btn">
                <Plus size={16} weight="bold" /> Novo
              </Link>
            </>
          )}
        </div>
      </div>

      {visibleItems.length === 0 ? (
        <div className="aj-card p-10 text-center text-slate-500">
          {filteredItems.length === 0 ? "Você ainda não publicou nenhum anúncio." : "Nenhum anúncio nesta visualização."}
        </div>
      ) : (
        <div className="space-y-3">
          {visibleItems.map((it) => {
            const isPaused = !it.is_active;
            const isSel = selected.includes(it.id);
            const isExpanded = expandedId === it.id;
            return (
              <div
                key={it.id}
                onClick={selectMode ? () => toggleSelect(it.id) : undefined}
                className={`aj-card p-3 ${isPaused ? "opacity-70 bg-slate-50" : ""} ${selectMode ? "cursor-pointer" : ""} ${isSel ? "ring-2 ring-[#004E98]" : ""}`}
                data-testid={`my-listing-${it.id}`}
              >
                <div className="flex gap-3 items-center">
                {selectMode && (
                  <div className="shrink-0 text-[#004E98]" data-testid={`select-${it.id}`}>
                    {isSel ? <CheckSquare size={24} weight="fill" /> : <Square size={24} weight="bold" className="text-slate-300" />}
                  </div>
                )}
                <div className="w-20 h-20 rounded-lg bg-slate-100 overflow-hidden flex-shrink-0 relative">
                  {(it.thumbnails?.[0] || it.images?.[0]) && <img src={fileUrl(it.thumbnails?.[0] || it.images[0])} alt="" className="w-full h-full object-cover" />}
                  {isPaused && (
                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                      <Pause size={20} weight="fill" className="text-white" />
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-bold text-sm leading-tight line-clamp-2 break-words" title={it.title} data-testid={`listing-title-${it.id}`}>{it.title}</div>
                    {isPaused && (
                      it.item_status === "PAUSED_SCHEDULED" && it.pause_start_date ? (
                        <span className="aj-badge bg-amber-100 text-amber-800 text-[10px] px-2 py-0.5 rounded-full font-bold" data-testid={`paused-range-${it.id}`}>
                          Pausado {it.pause_start_date.split("-").reverse().slice(0, 2).join("/")}–{(it.pause_end_date || "").split("-").reverse().slice(0, 2).join("/")}
                        </span>
                      ) : (
                        <span className="aj-badge bg-amber-100 text-amber-800 text-[10px] px-2 py-0.5 rounded-full font-bold">Pausado</span>
                      )
                    )}
                    {!isPaused && it.item_status === "PAUSED_SCHEDULED" && it.pause_start_date && (
                      <span className="aj-badge bg-blue-100 text-blue-800 text-[10px] px-2 py-0.5 rounded-full font-bold" data-testid={`scheduled-badge-${it.id}`}>
                        Pausa agendada {it.pause_start_date.split("-").reverse().slice(0, 2).join("/")}–{(it.pause_end_date || "").split("-").reverse().slice(0, 2).join("/")}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-slate-500">{it.city} · {it.category}</div>
                  <div className="font-heading font-black text-lg mt-1 text-[#004E98]">
                    {formatBRL(it.price_per_day)} <span className="text-[10px] text-slate-500 uppercase tracking-wider">/{unitLabelForItem(it)[0]}</span>
                  </div>
                  {(it.discount_rules?.length > 0 || it.weekend_promo?.active) && (
                    <div className="text-[10px] text-emerald-700 font-bold mt-0.5" data-testid={`has-rules-${it.id}`}>
                      {it.discount_rules?.length > 0 && `${it.discount_rules.length} desconto(s) progressivo(s)`}
                      {it.discount_rules?.length > 0 && it.weekend_promo?.active && " · "}
                      {it.weekend_promo?.active && "Promo fim de semana"}
                    </div>
                  )}
                </div>
                {!selectMode && (
                  <button
                    onClick={(e) => { e.stopPropagation(); setExpandedId(isExpanded ? null : it.id); }}
                    className="shrink-0 text-slate-500 hover:text-[#004E98] hover:bg-slate-100 rounded-full p-2 transition"
                    aria-label="Mais opções"
                    title="Mais opções (toque longo para Editar/Excluir)"
                    data-testid={`expand-actions-${it.id}`}
                  >
                    <DotsThreeVertical size={20} weight="bold" />
                  </button>
                )}
                </div>

                {/* V17.10 — Ações do card (Simular, Pausar, Duplicar, Editar, Excluir) — sem long-press */}
                {!selectMode && isExpanded && (
                  <div className="mt-3 pt-3 border-t border-slate-100 grid grid-cols-3 gap-1.5" data-testid={`actions-expanded-${it.id}`}>
                    <Link to={`/itens/${it.id}?simulate=1`} onClick={(e) => e.stopPropagation()}
                      className="text-[11px] py-2 px-2 rounded-lg border-2 border-amber-200 text-amber-700 hover:bg-amber-50 inline-flex items-center justify-center gap-1 font-bold"
                      data-testid={`simulate-${it.id}`}>
                      🧪 Simular
                    </Link>
                    <button
                      onClick={(e) => { e.stopPropagation(); (isPaused || it.item_status === "PAUSED_SCHEDULED" ? reactivate(it) : setPauseTarget(it)); }}
                      className={`text-[11px] py-2 px-2 rounded-lg border-2 inline-flex items-center justify-center gap-1 font-bold ${isPaused || it.item_status === "PAUSED_SCHEDULED" ? "border-emerald-200 text-emerald-700 hover:bg-emerald-50" : "border-amber-200 text-amber-700 hover:bg-amber-50"}`}
                      data-testid={`toggle-pause-${it.id}`}
                    >
                      {isPaused ? <><Play size={12} weight="bold" /> Reativar</> : it.item_status === "PAUSED_SCHEDULED" ? <><Play size={12} weight="bold" /> Cancelar</> : <><Pause size={12} weight="bold" /> Pausar</>}
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); setConfirm({ type: "duplicate", id: it.id, title: it.title }); }}
                      className="text-[11px] py-2 px-2 rounded-lg border-2 border-emerald-200 text-emerald-700 hover:bg-emerald-50 inline-flex items-center justify-center gap-1 font-bold"
                      data-testid={`duplicate-${it.id}`}>
                      <Copy size={12} weight="bold" /> Duplicar
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); nav(`/editar-anuncio/${it.id}`); }}
                      className="text-[11px] py-2 px-2 rounded-lg border-2 border-[#004E98]/30 text-[#004E98] hover:bg-[#004E98]/5 inline-flex items-center justify-center gap-1 font-bold col-span-1"
                      data-testid={`edit-${it.id}`}>
                      <PencilSimple size={12} weight="bold" /> Editar
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); setConfirm({ type: "delete", id: it.id, title: it.title }); }}
                      className="text-[11px] py-2 px-2 rounded-lg border-2 border-red-200 text-red-600 hover:bg-red-50 inline-flex items-center justify-center gap-1 font-bold col-span-2"
                      data-testid={`delete-${it.id}`}>
                      <Trash size={12} weight="bold" /> Excluir
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Barra de ação da seleção em massa */}
      {selectMode && (
        <div className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-slate-200 p-3 flex items-center gap-2 max-w-3xl mx-auto flex-wrap" data-testid="bulk-action-bar">
          <span className="text-sm font-bold text-slate-600 mr-auto">{selected.length} selecionado(s)</span>
          <button
            onClick={() => setShowBulk(true)}
            disabled={selected.length === 0}
            className="text-[12px] rounded-full border-2 border-slate-200 py-2 px-3 font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-40 inline-flex items-center gap-1"
            data-testid="open-bulk-rules-btn">
            <Stack size={14} weight="bold" /> Regras
          </button>
          <button
            onClick={() => setConfirm({ type: "bulk-duplicate", ids: [...selected] })}
            disabled={selected.length === 0}
            className="text-[12px] rounded-full border-2 border-emerald-200 text-emerald-700 hover:bg-emerald-50 py-2 px-3 font-bold disabled:opacity-40 inline-flex items-center gap-1"
            data-testid="bulk-duplicate-btn">
            <Copy size={14} weight="bold" /> Duplicar
          </button>
          <button
            onClick={() => setConfirm({ type: "bulk-delete", ids: [...selected] })}
            disabled={selected.length === 0}
            className="text-[12px] rounded-full border-2 border-red-200 text-red-600 hover:bg-red-50 py-2 px-3 font-bold disabled:opacity-40 inline-flex items-center gap-1"
            data-testid="bulk-delete-btn">
            <Trash size={14} weight="bold" /> Excluir
          </button>
        </div>
      )}

      {pauseTarget && (
        <PauseListingModal listing={pauseTarget} onClose={() => setPauseTarget(null)} onDone={load} />
      )}
      {showBulk && (
        <BulkRulesModal
          listingIds={selected}
          onClose={() => setShowBulk(false)}
          onDone={() => { exitSelectMode(); load(); }}
        />
      )}

      {/* V17.7: Confirmações (substitui window.confirm para funcionar em Capacitor/WebView) */}
      <ConfirmDialog
        open={confirm?.type === "delete"}
        onClose={() => setConfirm(null)}
        onConfirm={async () => { const id = confirm.id; setConfirm(null); await del(id); }}
        title="Excluir anúncio?"
        description={<>Esta ação é <b>definitiva</b> e não pode ser desfeita. Reservas em andamento não serão afetadas.{confirm?.title ? <div className="mt-1 text-slate-500 text-xs">Anúncio: <b>{confirm.title}</b></div> : null}</>}
        confirmLabel="Excluir"
        variant="danger"
        testid="confirm-delete-single"
      />
      <ConfirmDialog
        open={confirm?.type === "duplicate"}
        onClose={() => setConfirm(null)}
        onConfirm={async () => { const id = confirm.id; setConfirm(null); await duplicate(id); }}
        title="Duplicar anúncio?"
        description={<>A cópia será criada <b>pausada</b> e você será levado para editá-la.{confirm?.title ? <div className="mt-1 text-slate-500 text-xs">Anúncio: <b>{confirm.title}</b></div> : null}</>}
        confirmLabel="Duplicar"
        variant="success"
        testid="confirm-duplicate-single"
      />
      <ConfirmDialog
        open={confirm?.type === "bulk-duplicate"}
        onClose={() => setConfirm(null)}
        onConfirm={() => { setConfirm(null); bulkDuplicate(); }}
        title={`Duplicar ${confirm?.ids?.length || 0} anúncio(s)?`}
        description={<>As cópias começam <b>pausadas</b>. Você poderá ajustá-las depois em Meus Anúncios.</>}
        confirmLabel="Duplicar todos"
        variant="success"
        testid="confirm-bulk-duplicate"
      />
      <ConfirmDialog
        open={confirm?.type === "bulk-delete"}
        onClose={() => setConfirm(null)}
        onConfirm={() => { setConfirm(null); bulkDelete(); }}
        title={`Excluir ${confirm?.ids?.length || 0} anúncio(s)?`}
        description={<>Esta ação é <b>definitiva</b> e não pode ser desfeita.</>}
        confirmLabel="Excluir todos"
        variant="danger"
        testid="confirm-bulk-delete"
      />

      {/* V17.7: Barra de progresso p/ operações em lote */}
      <BulkProgressOverlay
        open={!!bulkProgress}
        title={bulkProgress?.title || ""}
        current={bulkProgress?.current || 0}
        total={bulkProgress?.total || 0}
        okCount={bulkProgress?.ok || 0}
        failCount={bulkProgress?.fail || 0}
        testid="bulk-progress"
      />
    </div>
  );
}
